import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import type { VaultUpload } from './useVaultUploads'

interface Props {
  upload: VaultUpload
  onRetry: () => void
  onDismiss: () => void
  /** The phone's edge-to-edge grid. */
  compact?: boolean
}

/** A file on its way into the Vault: its preview, where it is, and Retry when it failed. */
export function UploadTile({ upload: u, onRetry, onDismiss, compact = false }: Props) {
  const { t } = useTranslation('library')
  const failed = u.step === 'failed'
  const pct = Math.round(u.progress * 100)
  const label =
    u.step === 'staging' ? t('vault.upload.staging', { pct }) : t(`vault.upload.step.${u.step}`)
  return (
    <div
      className={`${compact ? 'lib-mtile' : 'lib-tile'} lib-uptile ${failed ? 'is-failed' : ''}`}
      aria-label={`${u.name} · ${failed ? t('vault.upload.step.failed') : label}`}
      title={u.error || u.name}
    >
      {u.preview ? (
        <img className="lib-tile-img lib-uptile-img" src={u.preview} alt="" />
      ) : (
        <FeatherIcon name={u.kind === 'video' ? 'film' : 'image'} size={compact ? 18 : 26} className="lib-uptile-icon" />
      )}
      <span className="lib-uptile-body">
        {failed ? (
          <>
            <FeatherIcon name="alert-circle" size={compact ? 14 : 18} />
            {!compact && <span className="lib-uptile-label">{u.error || t('vault.upload.step.failed')}</span>}
            <span className="lib-uptile-actions">
              <button type="button" className="lib-uptile-btn" onClick={onRetry} aria-label={t('vault.upload.retry', { name: u.name })}>
                <FeatherIcon name="rotate-cw" size={13} />
                {!compact && t('vault.upload.retryShort')}
              </button>
              <button type="button" className="lib-uptile-btn" onClick={onDismiss} aria-label={t('vault.upload.dismiss', { name: u.name })}>
                <FeatherIcon name="x" size={13} />
              </button>
            </span>
          </>
        ) : (
          <>
            {!compact && <span className="lib-uptile-label">{label}</span>}
            <span className="lib-uptile-bar" role="progressbar" aria-valuenow={u.step === 'staging' ? pct : undefined} aria-valuemin={0} aria-valuemax={100}>
              <span className={u.step === 'staging' || u.step === 'queued' ? '' : 'is-indeterminate'} style={u.step === 'staging' || u.step === 'queued' ? { width: `${pct}%` } : undefined} />
            </span>
          </>
        )}
      </span>
    </div>
  )
}
